import { useGetAllCbamSimulations } from '@/features/cbam-simulations-v2/hooks/cbamSimulations'
import {
  CbamSimulationGood,
  useGetAllCbamSimulationGoodsForOrganization
} from '@/features/cbam-simulations-v2/hooks/cbamSimulationGoods'
import {
  SupplierEmissionsReport,
  useGetAllSupplierEmissionsReportsForOrganization
} from '@/features/suppliers/hooks/supplierEmissionsReport'
import { useCalculateBatchLiability } from '@/features/cbam-reporting/hooks/cbamLiability'

function getSpecificDirectEmissionsFromSupplierEmissionsReport(
  good: CbamSimulationGood,
  supplierEmissionsReports: SupplierEmissionsReport[]
) {
  if (good.isUsingDefaultEmissions) {
    return null
  }

  return (
    supplierEmissionsReports.find(report => report.id === good.supplierEmissionsReportId)
      ?.specificDirectEmbeddedEmissions ?? 0
  )
}

function getSpecificIndirectEmissionsFromSupplierEmissionsReport(
  good: CbamSimulationGood,
  supplierEmissionsReports: SupplierEmissionsReport[]
) {
  if (good.isUsingDefaultEmissions) {
    return null
  }

  return (
    supplierEmissionsReports.find(report => report.id === good.supplierEmissionsReportId)
      ?.specificIndirectEmbeddedEmissions ?? 0
  )
}

export const useSimulationWithLiability = (
  organizationId: string,
  simulationId: string,
  goodFilters: {
    productionMethodCode: string
  } = {
    productionMethodCode: ''
  },
  cscfPercent?: number
) => {
  const cbamSimulationsQuery = useGetAllCbamSimulations(organizationId)
  const cbamSimulationGoodsQuery = useGetAllCbamSimulationGoodsForOrganization(organizationId)
  const isCbamSimulationGoodsLoading = cbamSimulationGoodsQuery.isLoading
  const supplierEmissionsReportsQuery =
    useGetAllSupplierEmissionsReportsForOrganization(organizationId)
  const cbamSimulations = cbamSimulationsQuery.data ?? []
  const cbamSimulationGoods = cbamSimulationGoodsQuery.data ?? []
  const supplierEmissionsReports = supplierEmissionsReportsQuery.data ?? []

  const goodsForSimulation = cbamSimulationGoods.filter(
    good => good.cbamSimulationId === simulationId
  )
  const filteredCbamSimulationGoods = goodsForSimulation.filter(good => {
    if (goodFilters.productionMethodCode !== '') {
      return good.productionMethodCode === goodFilters.productionMethodCode
    }
    return true
  })

  const simulation = cbamSimulations.find(sim => sim.id === simulationId)
  const cscfFromModifiers =
    simulation?.modifiers?.cscfPercentage != null && simulation.modifiers.cscfPercentage !== ''
      ? parseFloat(simulation.modifiers.cscfPercentage)
      : undefined
  const userCscfPercent = cscfPercent ?? cscfFromModifiers
  const applyHighCscf =
    userCscfPercent !== undefined && !Number.isNaN(userCscfPercent) && userCscfPercent !== 100

  const baseGoodsPayload = filteredCbamSimulationGoods.map(good => ({
    id: good.id,
    code: good.cnCode,
    country: good.country,
    volume: good.netMassInTonnes,
    isActualValues: !good.isUsingDefaultEmissions,
    importDate: good.importDate,
    ...(good.quarter != null && { quarter: good.quarter }),
    directEmissionsPerTonne: good.isUsingDefaultEmissions
      ? null
      : getSpecificDirectEmissionsFromSupplierEmissionsReport(good, supplierEmissionsReports),
    indirectEmissionsPerTonne: good.isUsingDefaultEmissions
      ? null
      : getSpecificIndirectEmissionsFromSupplierEmissionsReport(good, supplierEmissionsReports),
    cscfPercent: 100
  }))

  const highGoodsPayload = applyHighCscf
    ? filteredCbamSimulationGoods.map(good => ({
        id: good.id,
        code: good.cnCode,
        country: good.country,
        volume: good.netMassInTonnes,
        isActualValues: !good.isUsingDefaultEmissions,
        importDate: good.importDate,
        ...(good.quarter != null && { quarter: good.quarter }),
        directEmissionsPerTonne: good.isUsingDefaultEmissions
          ? null
          : getSpecificDirectEmissionsFromSupplierEmissionsReport(good, supplierEmissionsReports),
        indirectEmissionsPerTonne: good.isUsingDefaultEmissions
          ? null
          : getSpecificIndirectEmissionsFromSupplierEmissionsReport(good, supplierEmissionsReports),
        cscfPercent: userCscfPercent
      }))
    : []

  const importedGoodsExtended = filteredCbamSimulationGoods.map(good => ({
    ...good,
    emissionsReport:
      supplierEmissionsReports.find(report => report.id === good.supplierEmissionsReportId) ?? null
  }))

  const { data: liabilityDataBaseLow, isLoading: isBaseLowLoading } = useCalculateBatchLiability(
    { requests: baseGoodsPayload },
    JSON.stringify({ ...goodFilters, cscfPercent: 100 })
  )

  const { data: liabilityDataHigh, isLoading: isHighLoading } = useCalculateBatchLiability(
    { requests: highGoodsPayload },
    JSON.stringify({ ...goodFilters, cscfPercent: userCscfPercent })
  )

  const liabilityData = liabilityDataBaseLow
  const liabilityDataHighForScenario = applyHighCscf ? liabilityDataHigh : undefined
  const isBatchLiabilityLoading = isBaseLowLoading || (applyHighCscf && isHighLoading)

  return {
    liabilityData,
    liabilityDataBaseLow,
    liabilityDataHighForScenario,
    simulation,
    isBatchLiabilityLoading,
    importedGoodsExtended,
    isCbamSimulationGoodsLoading
  }
}
